import React, { useState } from 'react';
import styled from 'styled-components';

const TooltipContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const TooltipContent = styled.div<{
  $show: boolean;
  $position?: 'top' | 'bottom';
}>`
  position: absolute;
  background-color: ${({ theme }) => theme.background.secondary};
  color: ${({ theme }) => theme.text.title};
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  z-index: 1000;
  opacity: ${(props) => (props.$show ? 1 : 0)};
  visibility: ${(props) => (props.$show ? 'visible' : 'hidden')};
  transition:
    opacity 0.2s ease,
    visibility 0.2s ease;

  ${(props) =>
    props.$position === 'top'
      ? `
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
  `
      : `
    top: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
  `}

  /* Arrow */
  &::after {
    content: '';
    position: absolute;
    border: 4px solid transparent;
    ${(props) =>
      props.$position === 'top'
        ? `
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      border-top-color: ${props.theme.text.title};
    `
        : `
      bottom: 100%;
      left: 50%;
      transform: translateX(-50%);
      border-bottom-color: ${props.theme.text.title};
    `}
  }
`;

interface TooltipProps {
  children: React.ReactNode;
  content: string;
  position?: 'top' | 'bottom';
  show?: boolean;
  delay?: number;
}

const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  position = 'top',
  show = false,
  delay = 0,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    const id = setTimeout(() => setIsVisible(true), delay);
    setTimeoutId(id);
  };

  const handleMouseLeave = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    setIsVisible(false);
  };

  return (
    <TooltipContainer
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      <TooltipContent
        $show={show || isVisible}
        $position={position}
        role='tooltip'
      >
        {content}
      </TooltipContent>
    </TooltipContainer>
  );
};

export default Tooltip;
